/**
 * The words the Blocked drawer shows for a date exception (DNKADM-0389 U3) — pure, so the
 * drawer stays a component file and the wording is testable on its own.
 */
import type { ShiftLike } from '@/utils/shiftValidation';
import { blockedRangesOf, clockLabel, minutesToClock, toMinuteRanges } from '../blockOff';
import type { HourRange } from './derive';

export interface BlockedLabelInput {
  isAvailable: boolean;
  shifts: ShiftLike[];
  /** The day's operating window — the blocked ranges are the gaps inside it. */
  operating?: HourRange | null;
}

/** `9:00 AM – 12:00 PM` for a minute range; end-of-day reads as midnight. */
function rangeLabel(start: number, end: number): string {
  return `${clockLabel(minutesToClock(start))} – ${clockLabel(minutesToClock(end))}`;
}

/** The blocked ranges in words, or "Closed all day". */
export function blockedRangesLabel(block: BlockedLabelInput): string {
  if (!block.isAvailable) return 'Closed all day';
  if (!block.operating) return '—';
  const window = { start: block.operating.startHour * 60, end: block.operating.endHour * 60 };
  const gaps = blockedRangesOf({ isAvailable: true, shifts: block.shifts }, window);
  return gaps.length === 0 ? 'Nothing blocked inside the operating hours' : gaps.map((g) => rangeLabel(g.start, g.end)).join(', ');
}

/** The open hours in words, or "—" when closed. */
export function openHoursLabel(block: Pick<BlockedLabelInput, 'isAvailable' | 'shifts'>): string {
  if (!block.isAvailable) return '—';
  const ranges = toMinuteRanges(block.shifts);
  if (ranges.length === 0) return 'The location’s hours';
  return ranges.map((r) => rangeLabel(r.start, r.end)).join(', ');
}
